import React, { useMemo } from "react";

import { IEspmPropertyTypeDetailNode } from "../../../services/zeos-api/contracts/EspmPropertyType";
import { IMixedUseComponent } from "../../../services/zeos-api/contracts/Property";
import { ZeosLabel } from "../ZeosLabel";

interface IProps {
  componentOptions: IEspmPropertyTypeDetailNode[];
  value: IMixedUseComponent[];
  onChange: (rows: IMixedUseComponent[]) => void;
  disabled?: boolean;
  error?: string;
}

export const MixedUseAllocationTable: React.FC<IProps> = ({
  componentOptions,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const rows = value?.length ? value : [];

  const total = useMemo(() => {
    return rows.reduce((sum, row) => {
      const n = Number(row.allocationPercent);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [rows]);

  const totalOk = Math.round(total) === 100;

  const selectedDetailIds = useMemo(() => {
    return new Set(
      rows
        .map((row) => Number(row.detailId))
        .filter((id) => Number.isFinite(id) && id > 0)
    );
  }, [rows]);

  const hasDuplicateComponents = useMemo(() => {
    const detailIds = rows
      .map((row) => Number(row.detailId))
      .filter((id) => Number.isFinite(id) && id > 0);
    return detailIds.length !== new Set(detailIds).size;
  }, [rows]);

  const updateRow = (index: number, patch: Partial<IMixedUseComponent>) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    );
    onChange(next);
  };

  const addRow = () => {
    onChange([...rows, { detailId: 0, allocationPercent: undefined }]);
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const isOptionTaken = (detailId: number, rowIndex: number) => {
    const currentId = Number(rows[rowIndex]?.detailId);
    if (currentId === detailId) {
      return false;
    }
    return selectedDetailIds.has(detailId);
  };

  return (
    <div className="w-full border-l-4 border-zeos-blue/20 pl-4">
      <ZeosLabel label="Mixed-Use Composition" required />
      <div
        className={`rounded-lg border overflow-hidden ${error || hasDuplicateComponents ? "border-red-400" : "border-gray-200"}`}
      >
        <div className="grid grid-cols-[1fr_160px_40px] gap-3 px-3 py-2 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span>Component</span>
          <span>Allocation</span>
          <span />
        </div>
        {rows.map((row, index) => (
          <div
            key={`mu-${index}`}
            className="grid grid-cols-[1fr_160px_40px] gap-3 px-3 py-2 border-t border-gray-100 items-center"
          >
            <select
              disabled={disabled}
              value={row.detailId > 0 ? String(row.detailId) : ""}
              onChange={(e) =>
                updateRow(index, {
                  detailId: e.target.value ? Number(e.target.value) : 0,
                })
              }
              className="w-full rounded border border-[#929293] px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zeos-blue"
            >
              <option value="">Select component…</option>
              {componentOptions.map((opt) => (
                <option
                  key={opt.espmPropertyTypeDetailId}
                  value={opt.espmPropertyTypeDetailId}
                  disabled={isOptionTaken(
                    opt.espmPropertyTypeDetailId,
                    index
                  )}
                >
                  {opt.itemName}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1.5 min-w-0">
              <input
                type="text"
                inputMode="numeric"
                disabled={disabled}
                value={
                  row.allocationPercent != null
                    ? String(row.allocationPercent)
                    : ""
                }
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d.]/g, "");
                  updateRow(index, {
                    allocationPercent:
                      raw === "" ? undefined : Number(raw),
                  });
                }}
                className="w-full min-w-0 rounded border border-[#929293] px-2 py-2 text-sm text-right bg-white focus:outline-none focus:ring-2 focus:ring-zeos-blue"
              />
              <span className="text-gray-500 text-sm flex-none select-none" aria-hidden>
                %
              </span>
            </div>
            <button
              type="button"
              title="Remove"
              disabled={disabled}
              onClick={() => removeRow(index)}
              className="w-8 h-8 rounded flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <button
          type="button"
          disabled={disabled}
          onClick={addRow}
          className="border border-dashed border-[#929293] text-zeos-blue text-sm font-medium px-3 py-2 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          + Add component
        </button>
        <span
          className={`text-sm font-semibold px-3 py-1.5 rounded-full ${
            totalOk
              ? "bg-green-50 text-green-700"
              : "bg-amber-50 text-amber-700"
          }`}
        >
          {totalOk ? "Total 100% ✓" : `Total ${total}% — must equal 100%`}
        </span>
      </div>
      <p className="text-xs text-gray-500 mt-1.5">
        Allocate each use by share of gross floor area. Each component once;
        must total 100%.
      </p>
      {(error || hasDuplicateComponents) && (
        <div className="text-orange-500 text-xs mt-1">
          {error || "Each mixed-use component can only be selected once"}
        </div>
      )}
    </div>
  );
};
